<div class="page-wrapper">

  @if (loading && !order) {
  <div class="loading-state">
    <mat-spinner diameter="48"></mat-spinner>
    <p>Loading order status...</p>
  </div>
  } @else if (fetchError && !order) {
  <div class="error-card">
    <mat-icon class="error-icon">error_outline</mat-icon>
    <h2>Order Not Found</h2>
    <p>Could not find this order.</p>
  </div>
  } @else if (order) {
  <div class="status-card">

    <div class="order-header">
      <mat-icon class="brand-icon">sports_esports</mat-icon>
      <p class="target">{{ order.target_coins / 1000 | number }}K coins</p>
      <h1>{{ order.name }}</h1>
      @if (order.email) {
      <h3>{{ order.email }}</h3>
      }
    </div>

    <!-- Do-not-login notice: only while the order is in progress (a Failed order is paused and may need the user to act in-game) -->
    @if (showLoginWarning) {
    <div class="login-warning" role="alert">
      <mat-icon class="login-warning-icon">warning</mat-icon>
      <div class="login-warning-text">
        <strong>Do NOT log into your account until your order is finished</strong>
        <span>
          Do not sign in from the Web App, the Companion App, or a console/PC. Logging in
          will interrupt the transfer and may cause it to fail. We'll show "Done" here when it's safe to log in again.
        </span>
      </div>
    </div>
    }

    <!-- Status badge -->
    <div class="status-row">
      <mat-chip-set>
        <mat-chip [class]="'status-chip status-' + order.status">
          @if (order.status === 'Transferring') {
          <mat-spinner diameter="14" class="inline-spinner"></mat-spinner>
          }
          {{ statusLabel }}
        </mat-chip>
      </mat-chip-set>

      @if (order.status === 'Failed' && errorMessage) {
      <span class="error-hint">{{ errorMessage }}</span>
      }

      <span class="spacer"></span>

      @if (showResumeButton) {
      <button mat-stroked-button color="primary" [disabled]="resumeLoading" (click)="doResume()">
        @if (resumeLoading) { <mat-spinner diameter="16" class="inline-spinner"></mat-spinner> }
        @else { <mat-icon>play_arrow</mat-icon> }
        Resume
      </button>
      }
      @if (resumeError) {
      <span class="error-hint">{{ resumeError }}</span>
      }
    </div>

    <!-- Progress -->
    <div class="progress-section">
      <div class="progress-labels">
        <span>{{ order.transferred_coins / 1000 | number }}K / {{ order.target_coins / 1000 | number }}K coins</span>
        <span class="progress-pct">{{ progressPercentage | number:'1.0-0' }}%</span>
      </div>
      <mat-progress-bar mode="determinate" [value]="progressPercentage"
        [color]="(order.status === 'Failed') ? 'warn' : (order.status === 'Done') ? 'accent' : 'primary'">
      </mat-progress-bar>
    </div>

    <!-- Stats -->
    <div class="stats-grid">
      <div class="stat">
        <span class="stat-label">Order Date</span>
        <span class="stat-value">{{ order.created_at | date:'shortDate' }}</span>
      </div>
      <div class="stat">
        <span class="stat-label">Last Updated</span>
        <span class="stat-value">{{ order.updated_at | date:'shortTime' }}</span>
      </div>
    </div>

    <!-- Success message -->
    @if (order.status === 'Done') {
    <div class="success-banner">
      <mat-icon>check_circle</mat-icon>
      <span>Transfer complete! All {{ order.target_coins / 1000 | number }}K coins transferred.</span>
    </div>
    }

    <!-- Contract: Fix-it guide for resumable/correctable failure reasons -->
    @if (failureGuide) {
    <div class="guide-section">
      <button mat-stroked-button (click)="showGuide = !showGuide">
        <mat-icon>help_outline</mat-icon>
        {{ failureGuide.guideTitle }}
      </button>

      @if (showGuide) {
      <ol class="guide-steps">
        @for (step of failureGuide.steps; track step) {
        <li>{{ step }}</li>
        }
      </ol>
      }
    </div>
    }

    <!-- Contract: Fix Credentials -->
    @if (showCredsSection) {
    <div class="creds-section">
      <button mat-stroked-button
        (click)="showCredsForm = !showCredsForm; correctCredsError = null">
        <mat-icon>key</mat-icon>
        Fix Credentials
      </button>

      @if (showCredsForm) {
      <div class="creds-form">
        @if (contractCredsField === 'password') {
        <mat-form-field appearance="outline">
          <mat-label>Password</mat-label>
          <input matInput type="password" [(ngModel)]="credsPassword" required placeholder="Enter your new password" />
        </mat-form-field>
        }
        @if (contractCredsField === 'backup_code') {
        <mat-form-field appearance="outline">
          <mat-label>Backup Codes</mat-label>
          <input matInput [(ngModel)]="credsBackupCodes" (paste)="onBackupCodesPaste($event)" required
            placeholder="code1, code2, code3, code4, code5, code6" />
          <mat-hint>Comma-separated, at least one is required</mat-hint>
        </mat-form-field>
        }
        <button mat-flat-button color="primary" [disabled]="correctCredsLoading" (click)="doCorrectCreds()">
          @if (correctCredsLoading) { <mat-spinner diameter="16" class="inline-spinner"></mat-spinner> }
          Update Credentials
        </button>
        @if (correctCredsError) {
        <div class="error-banner">
          <mat-icon>error_outline</mat-icon>
          <span>{{ correctCredsError }}</span>
        </div>
        }
      </div>
      }
    </div>
    }

    <!-- Screenshots -->
    @if (startScreenshotUrl || endScreenshotUrl) {
    <div class="screenshots">
      @if (startScreenshotUrl) {
      <div class="screenshot-item">
        <p class="screenshot-label">Before Transfer</p>
        <img [src]="startScreenshotUrl" alt="Before transfer screenshot" class="screenshot-img" />
      </div>
      }
      @if (endScreenshotUrl) {
      <div class="screenshot-item">
        <p class="screenshot-label">After Transfer</p>
        <img [src]="endScreenshotUrl" alt="After transfer screenshot" class="screenshot-img" />
      </div>
      }
    </div>
    }

    @if (!isTerminal) {
    <p class="polling-hint">
      <mat-icon>autorenew</mat-icon>
      Auto-updating every 5 seconds
    </p>
    }

  </div>
  }

</div>